import { Button } from '../../../components/form/button';
import { Typography } from '../../../components/typography/typography';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Location } from '../../../clients/quipli-service/location/location';
import { OrderDocumentType, OrderType } from '../../../clients/quipli-service/order/order';
import Icon from '../../../components/icons/icon';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { OrderSection } from '../order-section/order-section';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { ProductImage } from '../../../components/image/product/product-image';
import { AssignUnitSelect } from '../assign-unit-select/assign-unit-select';
import { DateTime, Interval } from 'luxon';
import { displayHumanDuration, safeRound } from '../../../util/util';
import { EditableField, FieldCounter } from '../../../components/field/field';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import {
  CustomProductFeeRequest,
  CustomProductLineItem,
  FinancialSummary,
  ProductFeeLineItem,
  ProductLineItem,
  ProductSecurityDepositLineItem,
} from '../../../clients/quipli-service/financials/financials';
import {
  isOrderCustomInput,
  OrderInputItem,
  OrderInputRental,
} from '../../../clients/quipli-service/order/order-calculation';
import { toast } from '../../../util/toasts';
import { Spinner } from '../../../components/spinner/spinner';
import { ProductTrackingType } from '../../../clients/quipli-service/product/product';
import { ProductImageV2 } from '../../../components/image/product/product-image-v2';
import usePrevious from '../../../hooks/use-previous';
import { Badge } from '../../../components/badge/badge';

export type SelectedUnits = { productId: number; productLineItemId: string; units: number[] };
export type DeliveryDescription = { productId: number; text: string };

export const TableProducts = ({
  location,
  summary,
  orderInputItems,
  selectedUnits,
  setOrderInputs,
  setSelectedUnits,
  setCustomProductFees,
  isCycleBilled,
  saleSerialTrackingEnabled,
  deliveryDescriptions,
  documentType,
}: {
  location: Location;
  summary: FinancialSummary | undefined;
  orderInputItems: OrderInputItem[];
  selectedUnits: SelectedUnits[];
  setOrderInputs: Dispatch<SetStateAction<OrderInputItem[]>>;
  setSelectedUnits: Dispatch<SetStateAction<SelectedUnits[]>>;
  setCustomProductFees: Dispatch<SetStateAction<CustomProductFeeRequest[]>>;
  isCycleBilled: boolean;
  saleSerialTrackingEnabled: boolean;
  deliveryDescriptions: DeliveryDescription[];
  documentType: OrderDocumentType;
}) => {
  const { confirmAvailability, getConfiguration } = useAPI();
  // States
  const [data, setData] = useState<DataTableRow[]>([]);
  // Vars
  const columns: DataTableColumn[] = [
    { key: 'product', label: 'Product Name', className: '!py-[13px] !pl-[24px]' },
    { key: 'discount', label: 'Disc. (%)', className: '!py-[13px]' },
    { key: 'qty', label: 'Qty', className: '!py-[13px]' },
    { key: 'units', label: 'Assign Unit', className: '!py-[13px] max-w-[200px]' },
    { key: 'total', label: 'Total', className: '!py-[13px] text-right' },
    { key: 'delete', label: '', className: '!py-[13px]' },
  ];

  const removeOrderInput = (id: string) => {
    setOrderInputs(orderInputItems.filter((line) => line.id !== id));
  };

  const updateOrderInput = async (orderInput: OrderInputItem) => {
    try {
      !isOrderCustomInput(orderInput) && (await confirmAvailability({ ...orderInput, locationId: location.id }));
      setOrderInputs(orderInputItems.map((line) => (line.id === orderInput.id ? orderInput : line)));
    } catch (error: any) {
      toast(error.message, 'error');
    }
  };

  const [productImagesV2, setProductImagesV2] = useState<boolean>(true);
  useEffect(() => {
    getConfiguration('CONF_PRODUCT_IMAGES_V2').then((config) => {
      setProductImagesV2(config?.value === '1');
    });
  }, []);

  useEffect(() => {
    const productLineItems = (summary?.lineItems.filter((line) => line.type === 'Product') || []) as ProductLineItem[];
    const customLineItems = (summary?.lineItems.filter((line) => line.type === 'CustomProduct') ||
      []) as CustomProductLineItem[];
    const data = orderInputItems.flatMap((input) => {
      const lineItem = productLineItems.find((line) => line.id === input.id);
      if (lineItem && !isOrderCustomInput(input)) {
        const productFees = summary?.lineItems.filter(
          (line) => line.type === 'ProductFee' && line.productLineItemId === input.id,
        ) as ProductFeeLineItem[];
        const securityDeposit = summary?.lineItems.find(
          (li) => li.type === 'ProductSecurityDeposit' && li.productLineItemId === input.id,
        ) as ProductSecurityDepositLineItem | undefined;
        return [
          {
            fields: {
              product: (
                <ProductCell
                  input={input}
                  lineItem={lineItem}
                  productFees={productFees}
                  securityDeposit={securityDeposit}
                  onChange={updateOrderInput}
                  setCustomProductFees={setCustomProductFees}
                  productImagesV2={productImagesV2}
                  isCycleBilled={isCycleBilled}
                />
              ),
              discount: <DiscountCell input={input} onChange={updateOrderInput} />,
              qty: <QtyCell input={input} onChange={updateOrderInput} />,
              units: (
                <AssignUnitCell
                  input={input}
                  lineItem={lineItem}
                  locationId={location.id}
                  selectedUnits={selectedUnits}
                  setSelectedUnits={setSelectedUnits}
                  saleSerialTrackingEnabled={saleSerialTrackingEnabled}
                  documentType={documentType}
                />
              ),
              total: (
                <Typography type="text-sm" className="text-quipli-black-800 text-right">
                  {lineItem.amount.toFormat()}
                </Typography>
              ),
              delete: <DeleteCell id={input.id} onDelete={removeOrderInput} />,
            },
            mobileHeader: <Typography type="text-sm">Mobile</Typography>,
            extraRow: {
              content: (
                <DeliveryAndDatesCell
                  input={input}
                  location={location}
                  onChange={updateOrderInput}
                  deliveryDescription={deliveryDescriptions.find(({ productId }) => productId === input.productId)}
                />
              ),
              className: '!px-[24px] !py-[16px]',
            },
            key: input.id,
          },
        ];
      }

      const customLineItem = customLineItems.find((line) => line.id === input.id);
      if (customLineItem) {
        return [
          {
            fields: {
              product: <CustomProductCell input={input} lineItem={customLineItem} onChange={updateOrderInput} />,
              discount: (
                <Typography type="text-sm" className="text-grey-500">
                  N/A
                </Typography>
              ),
              qty: <QtyCell input={input} onChange={updateOrderInput} />,
              units: (
                <Typography type="text-sm" className="text-grey-500">
                  N/A
                </Typography>
              ),
              total: (
                <Typography type="text-sm" className="text-quipli-black-800 text-right">
                  {customLineItem.amount.toFormat()}
                </Typography>
              ),
              delete: <DeleteCell id={input.id} onDelete={removeOrderInput} />,
            },
            mobileHeader: <Typography type="text-sm">Mobile</Typography>,
            extraRow:
              input.type === OrderType.Rental
                ? {
                    content: (
                      <DeliveryAndDatesCell
                        input={input}
                        location={location}
                        onChange={updateOrderInput}
                        deliveryDescription={deliveryDescriptions.find(
                          ({ productId }) => productId === input.productId,
                        )}
                      />
                    ),
                    className: '!px-[24px] !py-[16px]',
                  }
                : {
                    content: <></>,
                    className: '',
                  },
            key: input.id,
          },
        ];
      }

      return [];
    });
    if (data.length === orderInputItems.length) {
      setData(data);
    }
  }, [summary, selectedUnits, deliveryDescriptions]);

  return (
    <OrderSection className="!px-0 !py-[6px]">
      <DataTable columnNames={columns} data={data} showAlwaysDesktop={true} />
    </OrderSection>
  );
};

const ProductCell = ({
  input,
  lineItem,
  productFees,
  securityDeposit,
  onChange,
  setCustomProductFees,
  productImagesV2,
  isCycleBilled,
}: {
  input: OrderInputItem;
  lineItem: ProductLineItem;
  productFees: ProductFeeLineItem[];
  securityDeposit?: ProductSecurityDepositLineItem;
  onChange: (orderInput: OrderInputItem) => void;
  setCustomProductFees: Dispatch<SetStateAction<CustomProductFeeRequest[]>>;
  productImagesV2: boolean;
  isCycleBilled: boolean;
}) => {
  return (
    <div className="flex gap-[12px] max-w-[281px]">
      <div className="flex items-center">
        {productImagesV2 ? (
          <ProductImageV2
            productId={lineItem.productId}
            profile="MINI"
            className="min-h-[48px] min-w-[48px] max-h-[48px] max-w-[48px] aspect-square  rounded-[6.85px] border-[0.87px] border-solid border-grey-200 overflow-hidden "
          />
        ) : (
          <ProductImage
            productId={lineItem.productId}
            profile="MINI"
            className="min-h-[48px] min-w-[48px] max-h-[48px] max-w-[48px] aspect-square  rounded-[6.85px] border-[0.87px] border-solid border-grey-200 overflow-hidden "
          />
        )}
      </div>

      <div className="whitespace-pre-wrap">
        <Typography type="text-sm" className="text-quipli-black-800">
          {lineItem.description}
        </Typography>

        {isCycleBilled && lineItem.transactionType === OrderType.Rental ? (
          <div>
            <Typography type="text-sm" className="text-grey-500">
              {lineItem.unitPrice.toFormat()}
            </Typography>
          </div>
        ) : (
          <EditableField
            type="number"
            label="Price"
            formattedValue={lineItem.unitPrice.toFormat()}
            value={lineItem.unitPrice.toUnit()}
            onChange={(newPrice) => {
              onChange({ ...input, customPrice: safeRound(newPrice * 100) });
              toast('Price updated.', 'success');
            }}
            onReset={() => {
              onChange({ ...input, customPrice: undefined });
              toast('Price updated to default.', 'success');
            }}
            canReset={lineItem.hasCustomAmount}
            resetPosition="right"
            dataCy="product-price-input"
          />
        )}

        <div className="flex flex-col gap-[3px] mt-[10px] w-full">
          {productFees.map((fee) => (
            <div className="flex flex-col w-full">
              <Typography type="text-sm" className="text-grey-500">
                {fee.description}
              </Typography>

              <EditableField
                type="number"
                label={fee.description}
                formattedValue={fee.amount.toFormat()}
                value={fee.amount.toUnit()}
                onChange={(newPrice) => {
                  setCustomProductFees((prev) => [
                    ...prev.filter(
                      (custom) =>
                        !(custom.feeId === fee.productFeeId && custom.productLineItemId === fee.productLineItemId),
                    ),
                    {
                      feeId: fee.productFeeId,
                      productId: fee.productId,
                      productLineItemId: fee.productLineItemId,
                      amount: safeRound(newPrice * 100),
                    } as CustomProductFeeRequest,
                  ]);
                  toast(`${fee.description} updated.`, 'success');
                }}
                onReset={() => {
                  setCustomProductFees((prev) =>
                    prev.filter(
                      (custom) =>
                        !(custom.feeId === fee.productFeeId && custom.productLineItemId === fee.productLineItemId),
                    ),
                  );
                  toast(`${fee.description} updated to default.`, 'success');
                }}
                canReset={fee.hasCustomAmount}
                resetPosition="right"
              />
            </div>
          ))}

          {securityDeposit && (
            <div className="flex flex-col w-full">
              <Typography type="text-sm" className="text-grey-500">
                {securityDeposit.description}
              </Typography>

              <div>
                <Typography type="text-sm" className="text-grey-500">
                  {securityDeposit.amount.toFormat()}
                </Typography>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomProductCell = ({
  input,
  lineItem,
  onChange,
}: {
  input: OrderInputItem;
  lineItem: CustomProductLineItem;
  onChange: (orderInput: OrderInputItem) => void;
}) => {
  return (
    <div className="flex gap-[12px] max-w-[281px]">
      <div className="flex items-center">
        <ProductImageV2
          productId={'placeholder'}
          profile="MINI"
          className="min-h-[48px] min-w-[48px] max-h-[48px] max-w-[48px] aspect-square  rounded-[6.85px] border-[0.87px] border-solid border-grey-200 overflow-hidden "
        />
      </div>

      <div className="whitespace-pre-wrap">
        <Typography type="text-sm" className="text-quipli-black-800">
          {lineItem.description}
        </Typography>

        <EditableField
          type="number"
          label="Price"
          formattedValue={lineItem.unitPrice.toFormat()}
          value={lineItem.unitPrice.toUnit()}
          onChange={(newPrice) => {
            onChange({ ...input, customPrice: safeRound(newPrice * 100) });
            toast('Price updated.', 'success');
          }}
          resetPosition="right"
          dataCy="custom-product-price-input"
        />
      </div>
    </div>
  );
};

const DiscountCell = ({
  input,
  onChange,
}: {
  input: OrderInputItem;
  onChange: (orderInput: OrderInputItem) => void;
}) => {
  const handleOnChange = (newDiscount: number) => {
    if (newDiscount < 0 || newDiscount > 100) {
      toast('Discount must be between 0% and 100%', 'error');
      return;
    }

    onChange({ ...input, manualDiscount: newDiscount });
  };

  return (
    <EditableField
      value={input.manualDiscount || 0}
      onChange={handleOnChange}
      maximumFractionDigits={2}
      type="number"
      label="Discount"
      formattedValue={`${(input.manualDiscount || 0).toFixed(2)}%`}
      noValueText="0.00%"
      placeholder="0.00%"
      canReset={(input.manualDiscount || 0) > 0}
      onReset={() => onChange({ ...input, manualDiscount: undefined })}
      resetPosition="right"
      dataCy="manual-discount-input"
    />
  );
};

const QtyCell = ({ input, onChange }: { input: OrderInputItem; onChange: (orderInput: OrderInputItem) => void }) => {
  return (
    <FieldCounter
      value={input.quantity}
      onChange={(newQty) => onChange({ ...input, quantity: newQty })}
      allowDecimals={input.type === OrderType.Sale}
      min={input.type === OrderType.Sale ? 0 : 1}
    />
  );
};

const AssignUnitCell = ({
  input,
  lineItem,
  locationId,
  selectedUnits,
  saleSerialTrackingEnabled,
  setSelectedUnits,
  documentType,
}: {
  input: OrderInputItem;
  lineItem: ProductLineItem;
  locationId: number;
  selectedUnits: SelectedUnits[];
  saleSerialTrackingEnabled: boolean;
  setSelectedUnits: Dispatch<SetStateAction<SelectedUnits[]>>;
  documentType: OrderDocumentType;
}) => {
  const { getProduct, getAvailableCount } = useAPI();

  const [trackingType, setTrackingType] = useState<ProductTrackingType>();
  const [qtyAvailable, setQtyAvailable] = useState<number | undefined>(0);
  const [stock, setStock] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showQtyWarning, setShowQtyWarning] = useState<boolean>(false);
  const prev = usePrevious(input);

  const checkAvailableQuantity = async () => {
    const maxEndDate = input.type === OrderType.Rental ? input.start.plus({ days: 28 }) : undefined;
    const available = await getAvailableCount(
      input.productId,
      locationId,
      input.type,
      input.type === OrderType.Rental ? input.start : DateTime.now(),
      input.type === OrderType.Rental ? input.end ?? maxEndDate! : DateTime.now(),
    );
    setQtyAvailable(available);
    setShowQtyWarning(input.quantity > available);

    if (input.quantity > available) {
      toast(`Warning: You are exceeding the available quantity of ${lineItem.description}.`, 'warning');
    }
  };

  useEffect(() => {
    const get = async () => {
      try {
        setIsLoading(true);
        const prod = await getProduct(input.productId);
        setTrackingType(prod.trackingType);

        if (prod.trackingType === ProductTrackingType.Quantity) {
          setStock(
            input.type === OrderType.Rental
              ? prod.stock?.find((s) => s.locationId === locationId)?.rentalStock || prod.rentalStock
              : prod.stock?.find((s) => s.locationId === locationId)?.saleStock || prod.saleStock,
          );
          await checkAvailableQuantity();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };

    get();
  }, []);

  useEffect(() => {
    if (input.type !== OrderType.Rental || trackingType !== ProductTrackingType.Quantity) return;

    const prevInput = prev as OrderInputRental;
    if (input.start === prevInput.start && input.end === prevInput.end && input.quantity === prevInput.quantity) return;

    checkAvailableQuantity();
  }, [trackingType, input]);

  return trackingType !== ProductTrackingType.NonInventory &&
    (input.type === OrderType.Rental || saleSerialTrackingEnabled) ? (
    isLoading ? (
      <Spinner />
    ) : trackingType === ProductTrackingType.SerialUnits ? (
      <AssignUnitSelect
        locationId={locationId}
        uniqueId={input.id}
        productId={input.productId}
        quantity={input.quantity}
        startDate={input.type === OrderType.Rental ? input.start : DateTime.now()}
        endDate={input.type === OrderType.Rental ? input.end : DateTime.now()}
        selectedUnitIds={selectedUnits.find((u) => u.productLineItemId === input.id)?.units || []}
        onChange={(units) =>
          setSelectedUnits((prev) => [
            ...prev.filter((u) => u.productLineItemId !== input.id),
            { productId: input.productId, productLineItemId: input.id, units },
          ])
        }
        orderProductType={input.type}
        disabled={documentType == OrderDocumentType.CreditMemo}
      />
    ) : (
      <div className="flex flex-col items-center">
        <Typography type="text-sm" className="text-quipli-black-800" dataCy="assign-unit-qty-available">
          {`${qtyAvailable && Number(qtyAvailable).toFixed(2)} ${
            stock && `of ${Number(stock).toFixed(2)}`
          } units available`}
        </Typography>

        {showQtyWarning && <Badge type="warning" text="Exceeding available quantity" dataCy="assign-unit-exceeded" />}
      </div>
    )
  ) : (
    <Typography type="text-sm" className="text-grey-500" dataCy="assign-unit-na">
      N/A
    </Typography>
  );
};

const DeleteCell = ({ id, onDelete }: { id: string; onDelete: (id: string) => void }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  return (
    <>
      <Button className="button-tertiary !py-0" onClick={() => setIsOpen(true)} dataCy="delete-cell-button">
        <Icon icon="Trash" stroke="var(--grey-500)" />
      </Button>

      <ConfirmModal
        prompt="Remove item?"
        additionalText="This cannot be undone"
        noLabel="Cancel"
        yesLabel="Remove"
        yesDataCy="delete-cell-confirm"
        yesButtonYellow
        isOpen={isOpen}
        headerIcon={
          <div className="flex justify-center">
            <Icon icon="InfoCircle" size="xlarge" stroke="var(--error-500)" />
          </div>
        }
        onConfirm={() => {
          onDelete(id);
          toast('Product removed.', 'success');
          setIsOpen(false);
        }}
        close={() => setIsOpen(false)}
      />
    </>
  );
};

const DeliveryAndDatesCell = ({
  input,
  location,
  onChange,
  deliveryDescription,
}: {
  input: OrderInputItem;
  location: Location;
  onChange: (orderInput: OrderInputItem) => void;
  deliveryDescription?: DeliveryDescription;
}) => {
  const duration = input.type === OrderType.Rental && input.end ? input.end.diff(input.start) : undefined;
  const [newStartDate, setNewStartDate] = useState<DateTime | undefined>(
    input.type === OrderType.Rental ? input.start : undefined,
  );
  const [newEndDate, setNewEndDate] = useState<DateTime | undefined>(
    input.type === OrderType.Rental && input.end ? input.end : undefined,
  );

  useEffect(() => {
    onChange({
      ...input,
      ...(input.type === OrderType.Rental && { start: newStartDate!, end: newEndDate! }),
    });
  }, [newStartDate, newEndDate]);

  return (
    <div className="flex w-full gap-x-[26px]">
      <div className="basis-1/4 min-w-[25%] flex flex-col gap-1 flex-wrap">
        <Typography type="text-sm" weight="medium" className="text-quipli-black-800">
          Delivery:
        </Typography>

        <Typography type="text-sm" className="text-grey-500 whitespace-pre-wrap" dataCy="delivery-description">
          {deliveryDescription?.text || '-'}
        </Typography>
      </div>

      {input.type === OrderType.Rental && (
        <>
          <div className="basis-1/4 flex flex-col">
            <div className="flex items-center gap-x-[2px]">
              <Typography type="text-sm" weight="medium" className="text-sm text-quipli-black-800 items-center flex">
                From:
              </Typography>
              <Icon icon="Calendar" size="small" />
            </div>

            <div className="flex items-center min-h-[38px]">
              <DateTimePicker
                value={newStartDate}
                onChange={setNewStartDate}
                timezone={location?.timezone}
                usePortal={true}
                customDisplay={
                  <Typography type="text-sm" className="text-grey-500">
                    {newStartDate?.toFormat('LLL-dd-yyyy hh:mm a')}
                  </Typography>
                }
                dataCy="rental-start-date"
              />
            </div>
          </div>

          <div className="basis-1/4 flex flex-col">
            <div className="flex items-center gap-x-[2px]">
              <Typography type="text-sm" weight="medium" className="text-sm text-quipli-black-800 items-center flex">
                To:
              </Typography>
              <Icon icon="Calendar" size="small" />
            </div>

            <div className="flex items-center min-h-[38px]">
              <DateTimePicker
                value={newEndDate}
                onChange={setNewEndDate}
                timezone={location?.timezone}
                disabledIntervals={[
                  Interval.before(newStartDate?.plus({ minutes: 30 }) || DateTime.now(), { years: 10 }),
                ]}
                usePortal={true}
                customDisplay={
                  <Typography type="text-sm" className="text-grey-500">
                    {newEndDate?.toFormat('LLL-dd-yyyy hh:mm a') || 'Not set'}
                  </Typography>
                }
                dataCy="rental-end-date"
              />
            </div>
          </div>

          {duration && (
            <div className="basis-1/4 flex flex-col">
              <Typography type="text-sm" weight="medium" className="text-sm text-quipli-black-800">
                Duration:
              </Typography>

              <div className="flex items-center min-h-[38px]">
                <Typography type="text-sm" className="text-grey-500" dataCy="rental-duration">
                  {displayHumanDuration(duration)}
                </Typography>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
